import { useEffect } from 'react';
import { Link } from 'react-router-dom';

export default function Labs() {
  useEffect(() => {
    document.title = 'Werner du Toit';
  }, []);

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;1,300;1,400&display=swap');

        .labs-page, .labs-landing, .labs-landing *, .labs-landing *::before, .labs-landing *::after { margin: 0; padding: 0; box-sizing: border-box; }

        .labs-page {
          --black: #0a0a0f;
          --off-white: #f0ece4;
          --rule: rgba(240,236,228,0.10);
          --nav-h: 64px;
          --nav-h-mobile: 60px;
          min-height: 100vh;
          background: var(--black);
          color: var(--off-white);
          font-family: 'Cormorant Garamond', Georgia, serif;
          position: relative;
        }

        /* Grain overlay */
        .labs-page::before {
          content: '';
          position: fixed;
          inset: 0;
          background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)' opacity='0.04'/%3E%3C/svg%3E");
          pointer-events: none;
          z-index: 300;
          opacity: 0.5;
        }

        .labs-landing {
          height: 100vh;
          display: grid;
          grid-template-columns: 1fr 1px 1fr 1px 1fr 1px 1fr;
        }

        .labs-divider {
          background: var(--rule);
        }

        .labs-panel {
          display: flex;
          flex-direction: column;
          justify-content: center;
          align-items: center;
          gap: 18px;
          padding: 48px 56px;
          text-decoration: none;
          color: inherit;
          position: relative;
          overflow: hidden;
        }

        .labs-panel::before {
          content: '';
          position: absolute;
          inset: 0;
          background: radial-gradient(ellipse at center, rgba(240,236,228,0.035) 0%, transparent 68%);
          opacity: 0;
          transition: opacity 0.7s ease;
          pointer-events: none;
        }

        .labs-panel:hover::before { opacity: 1; }

        .labs-inner {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 16px;
          opacity: 0;
          transform: translateY(10px);
          animation: labsFadeUp 0.9s ease forwards;
        }

        .labs-panel:nth-child(1) .labs-inner { animation-delay: 0.12s; }
        .labs-panel:nth-child(3) .labs-inner { animation-delay: 0.24s; }
        .labs-panel:nth-child(5) .labs-inner { animation-delay: 0.36s; }

        @keyframes labsFadeUp {
          to { opacity: 1; transform: translateY(0); }
        }

        .labs-name {
          font-size: clamp(38px, 5.2vw, 74px);
          font-weight: 300;
          letter-spacing: 0.04em;
          line-height: 1;
          color: rgba(240,236,228,0.85);
          transition: color 0.3s;
          text-align: center;
        }

        .labs-rule {
          width: 26px;
          height: 1px;
          background: rgba(240,236,228,0.18);
          transition: width 0.4s, background 0.3s;
        }

        .labs-tagline {
          font-size: clamp(11px, 1.2vw, 15px);
          font-style: italic;
          letter-spacing: 0.13em;
          color: rgba(240,236,228,0.25);
          transition: color 0.3s;
          text-align: center;
        }

        .labs-enter {
          margin-top: 6px;
          font-size: 9.5px;
          letter-spacing: 0.26em;
          text-transform: uppercase;
          font-family: 'Lucida Grande', Geneva, sans-serif;
          font-style: normal;
          color: rgba(240,236,228,0.12);
          transition: color 0.35s, transform 0.35s;
        }

        .labs-panel:hover .labs-name { color: #fff; }
        .labs-panel:hover .labs-rule { width: 48px; background: rgba(240,236,228,0.42); }
        .labs-panel:hover .labs-tagline { color: rgba(240,236,228,0.58); }
        .labs-panel:hover .labs-enter { color: rgba(240,236,228,0.48); transform: translateY(3px); }

        /* Mobile header — hidden on desktop */
        .labs-mobile-header {
          display: none;
        }

        /* Mobile */
        @media (max-width: 768px) {
          .labs-mobile-header {
            display: flex;
            align-items: center;
            gap: 8px;
            padding: 10px 18px;
            border-bottom: 1px solid rgba(240,236,228,0.07);
            height: 44px;
            flex-shrink: 0;
          }

          .labs-mobile-header img {
            width: 22px;
            height: 22px;
            border-radius: 5px;
            flex-shrink: 0;
          }

          .labs-mobile-header span {
            font-family: system-ui, sans-serif;
            font-size: 10px;
            font-weight: 300;
            letter-spacing: 0.16em;
            text-transform: uppercase;
            color: rgba(240,236,228,0.55);
          }

          .labs-page {
            display: flex;
            flex-direction: column;
            height: 100vh;
          }

          .labs-landing {
            grid-template-columns: 1fr;
            grid-template-rows: 1fr 1px 1fr 1px 1fr 1px 1fr;
            flex: 1;
            height: auto;
            padding-bottom: env(safe-area-inset-bottom, 0px);
          }

          .labs-divider {
            height: 1px;
            width: 100%;
          }

          .labs-panel {
            padding: 20px 28px;
          }

          .labs-inner {
            gap: 10px;
          }

          /* On mobile no hover exists — reveal text at resting state */
          .labs-name { color: rgba(240,236,228,0.92); }
          .labs-rule { width: 40px; background: rgba(240,236,228,0.32); }
          .labs-tagline { color: rgba(240,236,228,0.55); }
          .labs-enter { color: rgba(240,236,228,0.42); }

          /* Tap feedback as mobile substitute for hover glow */
          .labs-panel:active::before { opacity: 1; }
          .labs-panel:active .labs-name { color: #fff; }
          .labs-panel:active .labs-rule { width: 52px; background: rgba(240,236,228,0.55); }
          .labs-panel:active .labs-tagline { color: rgba(240,236,228,0.75); }
          .labs-panel:active .labs-enter { color: rgba(240,236,228,0.62); transform: translateY(3px); }
        }
      `}</style>

      <div className="labs-page">
        <div className="labs-mobile-header">
          <img src="/logo-zephyr.png" alt="Zephyr OS" style={{ clipPath: 'url(#squircle)' }} />
          <span>Zephyr OS</span>
        </div>
        <div className="labs-landing">
          <Link className="labs-panel" to="/gallery/cult">
            <div className="labs-inner">
              <div className="labs-name">Cult of One</div>
              <div className="labs-rule"></div>
              <div className="labs-tagline">Manifest of a Movement</div>
              <div className="labs-enter">Enter</div>
            </div>
          </Link>

          <div className="labs-divider"></div>

          <Link className="labs-panel" to="/gallery/framed">
            <div className="labs-inner">
              <div className="labs-name">Framed</div>
              <div className="labs-rule"></div>
              <div className="labs-tagline">A Life in Technicolour</div>
              <div className="labs-enter">Enter</div>
            </div>
          </Link>

          <div className="labs-divider"></div>

          <Link className="labs-panel" to="/travels">
            <div className="labs-inner">
              <div className="labs-name">Journeys</div>
              <div className="labs-rule"></div>
              <div className="labs-tagline">The World Unraveled</div>
              <div className="labs-enter">Enter</div>
            </div>
          </Link>

          <div className="labs-divider"></div>

          <Link className="labs-panel" to="/gallery/labs">
            <div className="labs-inner">
              <div className="labs-name">Labs</div>
              <div className="labs-rule"></div>
              <div className="labs-tagline">Awakening Interface</div>
              <div className="labs-enter">Enter</div>
            </div>
          </Link>
        </div>

      </div>
    </>
  );
}
